<script setup lang="ts">
import {
  ArabicKeys,
  GuldfuglKeyboard,
  type Keys as KeyboardKeys,
  RussianKeys
} from "@ordbogen-npm/guldfugl-keyboards";
import { storeToRefs } from "pinia";
import { computed, type ComputedRef } from "vue";
import { useI18n } from "vue-i18n";

import TgIconButton from "@/abc-dsk/components/IconButton/TgIconButton.vue";
import TgTooltip from "@/abc-dsk/components/Tooltip/TgTooltip.vue";
import { LANGUAGE_GROUP_ARABIC, LANGUAGE_GROUP_RUSSIAN } from "@/common/language-groups";
import IconBackspace from "@/components/icons/IconBackspace.vue";
import IconKeyboard from "@/components/icons/IconKeyboard.vue";
import IconCross from "@/components/icons/white/IconWhiteCross.vue";
import { MatomoHelper } from "@/helpers";
import { type KeyboardState, useFloatingKeyboardStore } from "@/stores";
import { useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

const emit = defineEmits<{
  key: [key: string];
  backspace: [];
  close: [];
}>();

const { t } = useI18n();

const floatingKeyboardStore = useFloatingKeyboardStore();

const EVENT_LABEL_OPEN = "open";
const EVENT_LABEL_CLOSE = "close";
const EVENT_LABEL_KEY_PRESS = "key-press";

const { activeLanguageGroup } = storeToRefs(useSettingsStore());

const usableKeyboard = computed<"arabic" | "russian" | undefined>(() => {
  if (activeLanguageGroup.value === LANGUAGE_GROUP_ARABIC) {
    return "arabic";
  }

  if (activeLanguageGroup.value === LANGUAGE_GROUP_RUSSIAN) {
    return "russian";
  }

  return undefined;
});

type Keyboard = {
  keys: KeyboardKeys;
  show: ComputedRef<boolean>;
  state: KeyboardState;
  texts: {
    open: string;
    close: string;
    header: string;
  };
  events: {
    category: MatomoEventCategory;
    action: MatomoEventAction;
  };
};

const keyboards: Keyboard[] = [
  {
    keys: ArabicKeys,
    show: computed(() => usableKeyboard.value === "arabic"),
    state: floatingKeyboardStore.getState("arabic"),
    texts: {
      open: t("searchBar.arabicKeyboard.keyboardTooltip.text", {
        action: t("searchBar.arabicKeyboard.keyboardTooltip.on")
      }),
      close: t("searchBar.arabicKeyboard.keyboardTooltip.text", {
        action: t("searchBar.arabicKeyboard.keyboardTooltip.off")
      }),
      header: t("searchBar.arabicKeyboard.headerText")
    },
    events: {
      category: MatomoEventCategory.ArabicKeyboard,
      action: MatomoEventAction.ArabicKeyboard
    }
  },
  {
    keys: RussianKeys,
    show: computed(() => usableKeyboard.value === "russian"),
    state: floatingKeyboardStore.getState("russian"),
    texts: {
      open: t("searchBar.russianKeyboard.keyboardTooltip.text", {
        action: t("searchBar.russianKeyboard.keyboardTooltip.on")
      }),
      close: t("searchBar.russianKeyboard.keyboardTooltip.text", {
        action: t("searchBar.russianKeyboard.keyboardTooltip.off")
      }),
      header: t("searchBar.russianKeyboard.headerText")
    },
    events: {
      category: MatomoEventCategory.RussianKeyboard,
      action: MatomoEventAction.RussianKeyboard
    }
  }
];

const setIsOpen = (state: KeyboardState, isOpen: boolean) => {
  state.isOpen = isOpen;

  if (isOpen === false) {
    emit("close");
  }
};

const onKey = (keyboard: Keyboard, key: string) => {
  MatomoHelper.sendEvent(keyboard.events.category, keyboard.events.action, EVENT_LABEL_KEY_PRESS);
  emit("key", key);
};

const onBackspace = (keyboard: Keyboard) => {
  MatomoHelper.sendEvent(keyboard.events.category, keyboard.events.action, EVENT_LABEL_KEY_PRESS);
  emit("backspace");
};
</script>

<template>
  <div class="keyboards arabic-fonts__RubikVariable">
    <template v-for="(keyboard, index) in keyboards" :key="index">
      <GuldfuglKeyboard
        v-if="keyboard.show.value"
        :keys="keyboard.keys"
        :is-open="keyboard.state.isOpen"
        :position-x="keyboard.state.posX"
        :position-y="keyboard.state.posY"
        :y-offset="58"
        @open="setIsOpen(keyboard.state, true)"
        @close="setIsOpen(keyboard.state, false)"
        @position-x="keyboard.state.posX = $event"
        @position-y="keyboard.state.posY = $event"
        @key="onKey(keyboard, $event)"
        @backspace="onBackspace(keyboard)"
      >
        <template #toggle="{ onToggle }">
          <TgTooltip
            :text="keyboard.state.isOpen ? keyboard.texts.close : keyboard.texts.open"
            class="keyboards__tooltip"
          >
            <button
              v-matomo-event:click="{
                category: keyboard.events.category,
                action: keyboard.events.action,
                label: keyboard.state.isOpen ? EVENT_LABEL_OPEN : EVENT_LABEL_CLOSE
              }"
              :aria-pressed="keyboard.state.isOpen"
              :aria-label="keyboard.state.isOpen ? keyboard.texts.close : keyboard.texts.open"
              class="keyboards__button"
              :class="{ 'keyboards__button--active': keyboard.state.isOpen }"
              type="button"
              @click="onToggle()"
            >
              <IconKeyboard />
            </button>
          </TgTooltip>
        </template>

        <template #header="{ id }">
          <span :id="id">{{ keyboard.texts.header }}</span>
        </template>
        <template #close>
          <TgIconButton
            v-matomo-event:click="{
              category: keyboard.events.category,
              action: keyboard.events.action,
              label: EVENT_LABEL_CLOSE
            }"
            variant="negative"
            type="button"
            @click="setIsOpen(keyboard.state, false)"
          >
            <IconCross role="presentation" />
          </TgIconButton>
        </template>

        <template #backspace>
          <IconBackspace role="presentation" width="24" height="24" />
        </template>
      </GuldfuglKeyboard>
    </template>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.keyboards {
  &__tooltip {
    margin-left: 8px;
  }

  &__button {
    margin-bottom: 0;
    padding: 11px 16px;
    border: 1px solid rgb(@theme-colors[@grey][50]);
    border-radius: 4px;
    outline: none;
    background-color: rgb(@theme-colors[@grey][10]);
    font-weight: 500;
    cursor: pointer;

    &:hover {
      border-color: rgb(@theme-colors[@grey][50]);
      background-color: rgb(@theme-colors[@grey][50]);
    }

    &:focus {
      border-color: rgb(@theme-colors[@grey][base]);
      box-shadow: 0 0 0 4px rgba(@theme-colors[@grey][base], 0.3);
    }

    &--active {
      border-color: rgb(@theme-colors[@primary][base]);
      background-color: rgb(@theme-colors[@primary][base]);
      color: @colors[@white];

      &:hover {
        border-color: rgb(@theme-colors[@primary][110]);
        background-color: rgb(@theme-colors[@primary][110]);
      }

      &:focus {
        box-shadow: 0 0 0 4px rgba(@theme-colors[@primary][base], 0.3);
      }
    }
  }

  ::v-deep(.guldfugl-keyboards) {
    border-radius: 8px;
    background-color: @colors[@white];
    box-shadow: 10px 10px 40px rgba(0, 0, 0, 0.1);
  }

  ::v-deep(.guldfugl-keyboards__header) {
    padding: 10px 15px;
    background-color: rgb(@theme-colors[@primary][base]);
    color: #fff;
  }

  ::v-deep(.guldfugl-keyboards__body) {
    padding: 8px;
  }

  ::v-deep(.guldfugl-keyboards__button) {
    height: 48px;
    border: 1px solid rgb(@theme-colors[@grey][50]);
    background-color: rgb(@theme-colors[@grey][10]);
    font-weight: 700;

    &:hover {
      border-color: rgb(@theme-colors[@grey][50]);
      background-color: rgb(@theme-colors[@grey][50]);
    }

    &:focus {
      border-color: rgb(@theme-colors[@grey][base]);
      box-shadow: 0 0 0 4px rgba(@theme-colors[@grey][base], 0.3);
    }

    &--active {
      border-color: rgb(@theme-colors[@primary][base]);
      background-color: rgb(@theme-colors[@primary][base]);
      color: @colors[@white];

      &:hover {
        border-color: rgb(@theme-colors[@primary][110]);
        background-color: rgb(@theme-colors[@primary][110]);
      }

      &:focus {
        box-shadow: 0 0 0 4px rgba(@theme-colors[@primary][base], 0.3);
      }
    }
  }

  ::v-deep(.guldfugl-keyboards__space-english) {
    font-weight: 500;
  }

  ::v-deep(.guldfugl-keyboards__space-locale) {
    font-weight: 700;
  }
}
</style>
